import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import FileTree from "../components/editor/FileTree";

// Monaco é pesado: carregado sob demanda para não atrasar o restante do app.
const CodeEditor = lazy(() => import("../components/editor/CodeEditor"));
const DiffViewer = lazy(() => import("../components/editor/DiffViewer"));
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  SkeletonRows,
  useToast,
} from "../components/ui";
import { fileService } from "../services/file.service";
import type { FileContents, WorkspaceEntry } from "../services/file.service";
import { projectService } from "../services/project.service";
import type { Project } from "../services/project.service";

type Prompt =
  | { kind: "create-file" | "create-directory"; parent: string }
  | { kind: "rename"; path: string }
  | { kind: "checkpoint" }
  | null;

/**
 * Editor de código do projeto: árvore de arquivos + Monaco + comparação
 * ANTES|DEPOIS. Toda gravação passa pela API — o navegador nunca escreve
 * no filesystem. O commit só acontece quando o usuário cria um checkpoint.
 */
export default function EditorPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  const [project, setProject] = useState<Project | null>(null);
  const [entries, setEntries] = useState<WorkspaceEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const [file, setFile] = useState<FileContents | null>(null);
  const [draft, setDraft] = useState("");
  const [before, setBefore] = useState<string | null>(null);
  const [mode, setMode] = useState<"edit" | "diff">("edit");
  const [saving, setSaving] = useState(false);

  const [prompt, setPrompt] = useState<Prompt>(null);
  const [promptValue, setPromptValue] = useState("");
  const [toDelete, setToDelete] = useState<string | null>(null);

  const activePath = params.get("path");
  const dirty = file !== null && draft !== file.contents;

  const refreshTree = useCallback(async () => {
    setEntries(await fileService.list(id));
  }, [id]);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        const [detail, list] = await Promise.all([projectService.get(id), fileService.list(id)]);
        if (!active) return;
        setProject(detail);
        setEntries(list);
      } catch {
        if (active) toast.error("Não foi possível carregar o projeto.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [id, toast]);

  useEffect(() => {
    if (!activePath) {
      setFile(null);
      setDraft("");
      setBefore(null);
      return;
    }

    let active = true;

    (async () => {
      try {
        const contents = await fileService.read(id, activePath);
        if (!active) return;
        setFile(contents);
        setDraft(contents.contents);
        setBefore(null);
      } catch (error) {
        if (active) toast.error(error instanceof Error ? error.message : "Não foi possível abrir o arquivo.");
      }
    })();

    return () => {
      active = false;
    };
  }, [id, activePath, toast]);

  function open(path: string) {
    if (dirty && !window.confirm("Há alterações não salvas. Deseja descartá-las?")) return;
    setMode("edit");
    setParams({ path }, { replace: true });
  }

  const save = useCallback(async () => {
    if (!file || saving) return;

    setSaving(true);
    try {
      const updated = await fileService.save(id, file.path, draft);
      setFile(updated);
      setDraft(updated.contents);
      await refreshTree();
      toast.success("Arquivo salvo.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  }, [file, draft, id, saving, refreshTree, toast]);

  async function showDiff() {
    if (!file) return;

    try {
      const original = await fileService.original(id, file.path);
      setBefore(original.contents);
      setMode("diff");
    } catch {
      setBefore("");
      setMode("diff");
    }
  }

  async function confirmPrompt() {
    const value = promptValue.trim();
    if (!prompt) return;

    try {
      if (prompt.kind === "checkpoint") {
        if (!value) return;
        const version = await fileService.checkpoint(id, value);
        toast.success(`Checkpoint criado: versão #${version.number}.`);
      } else if (prompt.kind === "create-file") {
        if (!value) return;
        const path = prompt.parent ? `${prompt.parent}/${value}` : value;
        await fileService.create(id, path, "");
        await refreshTree();
        setParams({ path }, { replace: true });
        toast.success("Arquivo criado.");
      } else if (prompt.kind === "create-directory") {
        if (!value) return;
        await fileService.createDirectory(id, prompt.parent ? `${prompt.parent}/${value}` : value);
        await refreshTree();
        toast.success("Pasta criada.");
      } else if (prompt.kind === "rename") {
        if (!value) return;
        const parent = prompt.path.split("/").slice(0, -1).join("/");
        const target = parent ? `${parent}/${value}` : value;
        await fileService.move(id, prompt.path, target);
        await refreshTree();
        if (activePath === prompt.path) setParams({ path: target }, { replace: true });
        toast.success("Renomeado.");
      }

      setPrompt(null);
      setPromptValue("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Operação não permitida.");
    }
  }

  async function remove() {
    if (!toDelete) return;

    try {
      await fileService.remove(id, toDelete);
      await refreshTree();
      if (activePath === toDelete) setParams({}, { replace: true });
      toast.success("Excluído.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível excluir.");
    } finally {
      setToDelete(null);
    }
  }

  const promptTitle = useMemo(() => {
    if (!prompt) return "";
    if (prompt.kind === "create-file") return "Novo arquivo";
    if (prompt.kind === "create-directory") return "Nova pasta";
    if (prompt.kind === "rename") return "Renomear";
    return "Criar checkpoint";
  }, [prompt]);

  if (loading) return <SkeletonRows rows={6} />;

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-ink">Editor · {project?.name}</h1>
          <p className="font-mono text-[11px] text-ink-muted">{activePath ?? "Nenhum arquivo aberto"}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {dirty && <Badge tone="warn">Modificado</Badge>}
          <Button size="sm" variant="secondary" onClick={() => setMode("edit")} disabled={mode === "edit"}>
            Editar
          </Button>
          <Button size="sm" variant="secondary" onClick={showDiff} disabled={!file}>
            Comparar
          </Button>
          <Button size="sm" onClick={save} loading={saving} disabled={!file || !dirty}>
            Salvar (Ctrl+S)
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setPromptValue("");
              setPrompt({ kind: "checkpoint" });
            }}
          >
            Checkpoint
          </Button>
          <Link to={`/projects/${id}/builder`} className="text-xs text-brand hover:underline">
            Voltar ao Builder
          </Link>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[260px_1fr]">
        <aside className="min-h-0 rounded-lg border border-line/60 bg-surface-raised/40 p-2">
          <FileTree
            entries={entries}
            activePath={activePath}
            dirtyPaths={dirty && file ? [file.path] : []}
            onOpen={open}
            onCreateFile={(parent) => {
              setPromptValue("");
              setPrompt({ kind: "create-file", parent });
            }}
            onCreateDirectory={(parent) => {
              setPromptValue("");
              setPrompt({ kind: "create-directory", parent });
            }}
            onRename={(path) => {
              setPromptValue(path.split("/").pop() ?? "");
              setPrompt({ kind: "rename", path });
            }}
            onDelete={(path) => setToDelete(path)}
          />
        </aside>

        <section className="min-h-0 overflow-hidden rounded-lg border border-line/60 bg-[#0d1117]">
          {!file && (
            <EmptyState
              title="Selecione um arquivo"
              description="Escolha um arquivo na árvore à esquerda para editar o código."
            />
          )}

          {file && file.is_binary && (
            <EmptyState title="Arquivo binário" description="Este tipo de arquivo não pode ser editado como texto." />
          )}

          {file && !file.is_binary && (
            <Suspense fallback={<p className="p-4 text-xs text-ink-muted">Carregando editor…</p>}>
              {mode === "edit" ? (
                <CodeEditor path={file.path} value={draft} onChange={setDraft} onSave={save} />
              ) : (
                <DiffViewer path={file.path} before={before ?? ""} after={draft} />
              )}
            </Suspense>
          )}
        </section>
      </div>

      <Dialog
        open={prompt !== null}
        onClose={() => setPrompt(null)}
        title={promptTitle}
        description={
          prompt?.kind === "checkpoint"
            ? "Um checkpoint cria um commit Git com o estado atual do projeto."
            : "Use apenas o nome; a pasta atual é mantida."
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setPrompt(null)}>
              Cancelar
            </Button>
            <Button onClick={confirmPrompt}>Confirmar</Button>
          </>
        }
      >
        <Input
          value={promptValue}
          autoFocus
          placeholder={prompt?.kind === "checkpoint" ? "Descrição da alteração" : "nome-do-arquivo.tsx"}
          onChange={(event) => setPromptValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void confirmPrompt();
          }}
        />
      </Dialog>

      <ConfirmDialog
        open={toDelete !== null}
        title="Excluir do projeto"
        description={toDelete ? `“${toDelete}” será removido do workspace.` : undefined}
        confirmLabel="Excluir"
        destructive
        onConfirm={remove}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
